import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { PaymentMethod, PaymentStatus, CreatePaymentRequest, PaymentMethodLabels, PaymentStatusLabels } from '../types/payment';
import { paymentService } from '../services/paymentService';
import { formatCurrency } from '../utils/currency';

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: string;
  invoiceId: string;
  invoiceNumber: string;
  outstandingAmount: number;
  currency: string;
}

interface PaymentFormData {
  amount: number;
  paymentDate: string;
  method: PaymentMethod;
  status: PaymentStatus;
  transactionReference: string;
  notes: string;
}

export const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  invoiceId,
  invoiceNumber,
  outstandingAmount,
  currency
}) => {
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<PaymentFormData>({
    defaultValues: {
      amount: outstandingAmount,
      paymentDate: new Date().toISOString().split('T')[0],
      method: PaymentMethod.BANK_TRANSFER,
      status: PaymentStatus.PENDING,
      transactionReference: '',
      notes: ''
    }
  });

  const watchedAmount = watch('amount');

  const onSubmit = async (data: PaymentFormData) => {
    setIsLoading(true);
    try {
      const paymentData: CreatePaymentRequest = {
        amount: data.amount,
        paymentDate: data.paymentDate,
        method: data.method,
        status: data.status,
        transactionReference: data.transactionReference || undefined,
        notes: data.notes || undefined
      };

      await paymentService.createPayment(companyId, invoiceId, paymentData);
      onSuccess();
      reset();
      onClose();
    } catch (error) {
      console.error('Ошибка создания платежа:', error);
      alert('Ошибка создания платежа');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Добавить платеж</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Инвойс:</strong> {invoiceNumber}
          </p>
          <p className="text-sm text-blue-800">
            <strong>Остаток к доплате:</strong> {formatCurrency(outstandingAmount, currency)}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Сумма платежа *
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={outstandingAmount}
              {...register('amount', { 
                required: 'Сумма обязательна',
                min: { value: 0.01, message: 'Сумма должна быть больше 0' },
                max: { value: outstandingAmount, message: 'Сумма не может превышать остаток' }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
            )}
            {watchedAmount > outstandingAmount && (
              <p className="text-orange-500 text-sm mt-1">
                Сумма превышает остаток к доплате
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата платежа *
            </label>
            <input
              type="date"
              {...register('paymentDate', { required: 'Дата обязательна' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.paymentDate && (
              <p className="text-red-500 text-sm mt-1">{errors.paymentDate.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Метод платежа *
            </label>
            <select
              {...register('method', { required: 'Метод обязателен' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(PaymentMethodLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.method && (
              <p className="text-red-500 text-sm mt-1">{errors.method.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Статус *
            </label>
            <select
              {...register('status', { required: 'Статус обязателен' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(PaymentStatusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.status && (
              <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ссылка на транзакцию
            </label>
            <input
              type="text"
              {...register('transactionReference')}
              placeholder="Номер транзакции, ссылка и т.д."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Примечания
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              placeholder="Дополнительная информация о платеже"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Создание...' : 'Создать платеж'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
